<template>
  <div class="is-pc pc-detail">
    <header class="pc-header">
      <div class="header-left">
        <h1>三高体检 · 内勤管理</h1>
        <span class="page-title">客户问卷详情</span>
      </div>
      <button class="back-btn" @click="goBack">返回列表</button>
    </header>

    <main class="pc-body">
      <div v-if="loading" class="state-cell">
        <div class="loading-spinner blue"></div>
        <p>加载中...</p>
      </div>

      <template v-else-if="detail">
        <div class="detail-layout">
          <aside class="detail-side">
            <section class="info-card">
              <div class="info-top">
                <span class="info-name">{{ detail.personName || "匿名用户" }}</span>
                <span class="audit-badge" :class="`audit-${detail.auditStatus}`">
                  {{ auditText(detail.auditStatus) }}
                </span>
              </div>
              <div class="info-row"><span class="info-label">手机号</span><span>{{ detail.phone || "-" }}</span></div>
              <div class="info-row"><span class="info-label">性别</span><span>{{ detail.gender || "-" }}</span></div>
              <div class="info-row"><span class="info-label">年龄</span><span>{{ detail.age != null ? detail.age + "岁" : "-" }}</span></div>
              <div class="info-row"><span class="info-label">身份证</span><span>{{ maskIdCard(detail.idCard) }}</span></div>
              <div class="info-row"><span class="info-label">业务员</span><span>{{ detail.salesmanName || "-" }}</span></div>
              <div class="info-row"><span class="info-label">提交时间</span><span>{{ formatTime(detail.submitTime) }}</span></div>
              <div class="info-row"><span class="info-label">问卷</span><span>{{ questionnaireText }}</span></div>
              <div class="info-row"><span class="info-label">填写次数</span><span>{{ detail.revisionNo || "-" }}</span></div>
              <div class="info-row"><span class="info-label">初筛结果</span><span>{{ qualifiedText(detail) }}</span></div>
              <screening-review-notice :record="detail" />
            </section>

            <section v-if="!detail.screeningStatus && detail.flags && detail.flags.length" class="flag-card">
              <p class="side-title">风险提示</p>
              <div class="flag-list">
                <span v-for="(flag, i) in detail.flags" :key="i" class="flag-tag">{{ flag }}</span>
              </div>
            </section>
          </aside>

          <section class="detail-main">
            <section v-for="(group, gi) in answerGroups" :key="gi" class="answer-card">
              <p class="side-title">{{ group.title }}</p>
              <div v-for="(item, ii) in group.items" :key="ii" class="answer-row">
                <div class="answer-label">
                  <small v-if="item.groupTitle">{{ item.groupTitle }}</small>
                  <p>{{ item.questionTitle }}</p>
                </div>
                <div class="answer-value">{{ item.displayValue || "—" }}</div>
              </div>
            </section>

            <div v-if="!answerGroups.length" class="state-cell">
              <p>暂无问卷填写内容</p>
            </div>
          </section>
        </div>
      </template>

      <div v-else class="state-cell">
        <p>{{ loadError || "未找到客户信息" }}</p>
      </div>
    </main>
  </div>
</template>

<script>
import { pcToast } from "./pc-ui";
import { qualifiedText } from "./customer-data";
import ScreeningReviewNotice from '../screening-review-notice.vue';
import { getSanGaoStaffCustomerDetail } from "@/api/sanGaoStaff";

export default {
  name: "SanGaoStaffPcCustomerDetail",
  components: { ScreeningReviewNotice },
  data() {
    return {
      loading: true,
      detail: null,
      loadError: "",
    };
  },
  computed: {
    answerGroups() {
      const groups = [];
      const groupMap = {};
      (this.detail && this.detail.answerItems ? this.detail.answerItems : []).forEach((item) => {
        const title = item.sectionTitle || "问卷填写内容";
        if (!groupMap[title]) {
          groupMap[title] = { title, items: [] };
          groups.push(groupMap[title]);
        }
        groupMap[title].items.push(item);
      });
      return groups;
    },
    questionnaireText() {
      if (!this.detail) return "-";
      const name = this.detail.questionnaireName || "问卷";
      const version = this.detail.questionnaireVersion ? `（版本${this.detail.questionnaireVersion}）` : "";
      const disease = this.diseaseText(this.detail.disease);
      return `${name}${version}${disease ? ` · ${disease}` : ""}`;
    },
  },
  created() {
    this.loadDetail();
  },
  methods: {
    qualifiedText,
    async loadDetail() {
      const customerId = this.$route.query.customerId;
      if (!customerId) {
        this.loadError = "缺少客户参数";
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const res = await getSanGaoStaffCustomerDetail({ customerId });
        if (res && res.code === 200 && res.data) {
          this.detail = res.data;
        } else {
          this.loadError = (res && res.msg) || "未找到客户信息";
        }
      } catch (e) {
        this.loadError = "网络异常，请稍后重试";
        pcToast(this.loadError);
      } finally {
        this.loading = false;
      }
    },
    diseaseText(disease) {
      if (!disease) return "";
      if (disease === "hypertension") return "高血压";
      if (disease === "diabetes") return "糖尿病";
      if (disease === "uric") return "高尿酸";
      return disease;
    },
    auditText(status) {
      if (status === 0) return "待审核";
      if (status === 1) return "已通过";
      if (status === 2) return "已驳回";
      if (status === 3) return "已支付";
      if (status === 4) return "已退款";
      return "-";
    },
    maskIdCard(idCard) {
      if (!idCard) return "-";
      if (idCard.includes("*")) return idCard;
      if (idCard.length === 18) return `${idCard.slice(0, 6)}********${idCard.slice(-4)}`;
      if (idCard.length === 15) return `${idCard.slice(0, 4)}********${idCard.slice(-3)}`;
      return "****";
    },
    formatTime(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      const pad = (n) => (n < 10 ? `0${n}` : String(n));
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    goBack() {
      this.$router.replace("/sanGao/staff/pc/index");
    },
  },
};
</script>

<style lang="less" scoped>
.is-pc.pc-detail {
  min-height: 100vh;
  background: #eef5fa;
}

.is-pc .pc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 60px;
  padding: 0 28px;
  background: linear-gradient(100deg, #0a6bb8, #168de0);
  color: #fff;
}

.is-pc .header-left {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.is-pc .pc-header h1 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.is-pc .page-title {
  font-size: 14px;
  opacity: 0.9;
}

.is-pc .back-btn {
  height: 30px;
  padding: 0 16px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 15px;
  font-size: 13px;
  color: #fff;
  background: transparent;
  cursor: pointer;
}

.is-pc .back-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.is-pc .pc-body {
  max-width: 1240px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}

.is-pc .detail-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  align-items: start;
}

.is-pc .detail-side,
.is-pc .detail-main {
  min-width: 0;
}

.is-pc .info-card,
.is-pc .flag-card,
.is-pc .answer-card {
  margin-bottom: 16px;
  padding: 16px 18px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(12, 91, 147, 0.08);
}

.is-pc .info-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.is-pc .info-name {
  font-size: 17px;
  font-weight: 700;
  color: #163b5c;
}

.is-pc .audit-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
}

.is-pc .audit-0 {
  color: #b6820b;
  background: #fff5d6;
}

.is-pc .audit-1 {
  color: #1b8a4b;
  background: #dff7e8;
}

.is-pc .audit-2 {
  color: #b14545;
  background: #fde0e0;
}

.is-pc .audit-3 {
  color: #1670c0;
  background: #ddf0fd;
}

.is-pc .audit-4 {
  color: #8a63c9;
  background: #efe6fc;
}

.is-pc .info-row {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 13px;
  color: #4a6a82;
}

.is-pc .info-label {
  flex-shrink: 0;
  margin-right: 16px;
  color: #93a8b8;
}

.is-pc .side-title {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #163b5c;
}

.is-pc .flag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.is-pc .flag-tag {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  color: #b6820b;
  background: #fff5d6;
}

.is-pc .answer-row {
  display: flex;
  align-items: baseline;
  padding: 10px 0;
  border-bottom: 1px solid #eef4f9;
}

.is-pc .answer-row:last-child {
  border-bottom: 0;
}

.is-pc .answer-label {
  flex: 0 0 42%;
  margin-right: 16px;
}

.is-pc .answer-label small {
  display: block;
  font-size: 11px;
  color: #93a8b8;
}

.is-pc .answer-label p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #4a6a82;
  word-break: break-all;
}

.is-pc .answer-value {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  word-break: break-all;
  color: #163b5c;
}

.is-pc .state-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 72px 0;
  color: #7c8d9c;
}

.is-pc .state-cell p {
  margin: 0;
  font-size: 13px;
}

.is-pc .loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(22, 141, 224, 0.25);
  border-top-color: #168de0;
  border-radius: 50%;
  animation: pc-spin 0.8s linear infinite;
}

@keyframes pc-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
